import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { LogOut, Settings } from 'lucide-react';
import { defaultDashboardConfigs } from '../../config/dashboardConfig';
import { getConfiguredSubjects } from '../../utils/semesterConfig';

// Import all widgets
import WidgetWrapper from '../widgets/WidgetWrapper';
import StatsOverviewWidget from '../widgets/StatsOverviewWidget';
import StudentSearchWidget from '../widgets/StudentSearchWidget';
import MenteeListWidget from '../widgets/MenteeListWidget';
import AttendanceCardWidget from '../widgets/AttendanceCardWidget';
import AchievementApprovalsWidget from '../widgets/AchievementApprovalsWidget';
import LeaveApprovalsWidget from '../widgets/LeaveApprovalsWidget';
import MessagingWidget from '../widgets/MessagingWidget';
import StaffManagementWidget from '../widgets/StaffManagementWidget';
import FacultyRolesWidget from '../widgets/FacultyRolesWidget';
import AnalyticsChartsWidget from '../widgets/AnalyticsChartsWidget';
import SheetsSyncWidget from '../widgets/SheetsSyncWidget';
import AttendanceEntryWidget from '../widgets/AttendanceEntryWidget';
import BatchAssignmentsWidget from '../widgets/BatchAssignmentsWidget';
import MentorOverviewWidget from '../widgets/MentorOverviewWidget';
import UnassignedStudentsWidget from '../widgets/UnassignedStudentsWidget';
import DivisionAnalyticsWidget from '../widgets/DivisionAnalyticsWidget';
import MenteeDetailsWidget from '../widgets/MenteeDetailsWidget';
import AttendanceAlertsWidget from '../widgets/AttendanceAlertsWidget';
import StudentProfileWidget from '../widgets/StudentProfileWidget';
import IaTest1CardWidget from '../widgets/IaTest1CardWidget';
import IaTest2CardWidget from '../widgets/IaTest2CardWidget';
import QuickActionsWidget from '../widgets/QuickActionsWidget';
import AchievementsListWidget from '../widgets/AchievementsListWidget';
import LeaderboardWidget from '../widgets/LeaderboardWidget';
import NotificationsWidget from '../widgets/NotificationsWidget';
import StudentIdCardWidget from '../widgets/StudentIdCardWidget';
import WidgetMaker from '../widgets/WidgetMaker';
import StudentListWidget from '../widgets/StudentListWidget';
import MenteeYaadiWidget from '../widgets/MenteeYaadiWidget';
import StudentManagementWidget from '../widgets/StudentManagementWidget';
import SemesterSubjectsWidget from '../widgets/SemesterSubjectsWidget';
import ParentsMeetManagementWidget from '../widgets/ParentsMeetManagementWidget';
import CourseMarksEntryWidget from '../widgets/CourseMarksEntryWidget';
import CourseAttendanceEntryWidget from '../widgets/CourseAttendanceEntryWidget';
import FacultyProfileWidget from '../widgets/FacultyProfileWidget';
import SystemArchiveWidget from '../widgets/SystemArchiveWidget';
import SubmissionStatusWidget from '../widgets/SubmissionStatusWidget';

// CUSTOM WIDGETS IMPORTS
import NoticesAndAnnouncementsWidget from '../widgets/NoticesAndAnnouncementsWidget';
import WarningLettersWidget from '../widgets/WarningLettersWidget';
import ForminfoWidget from '../widgets/ForminfoWidget';
import DivisionAssignmentsWidget from '../widgets/DivisionAssignmentsWidget';
import PendingAchievementsWidget from '../widgets/PendingAchievementsWidget';
import AttendanceReportWidget from '../widgets/AttendanceReportWidget';
import TopperformersWidget from '../widgets/TopperformersWidget';
import NanoMessagingWidget from '../widgets/NanoMessagingWidget';
import SlowAverageFastLearnersWidget from '../widgets/SlowAverageFastLearnersWidget';
// Auto-generated custom widget imports will be added here by the backend

const requiredWidgetsByRole = {
  hod: [
    { id: 'faculty-roles', enabled: true, order: 3, size: 'full' },
    { id: 'student-management', enabled: true, order: 4, size: 'full' },
    { id: 'semester-subjects', enabled: true, order: 5, size: 'full' },
    { id: 'divisionassignments', enabled: true, order: 6, size: 'full' },
    { id: 'system-archive', enabled: true, order: 7, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 8, size: 'full' }
  ],
  classCoordinator: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'attendance-entry', enabled: true, order: 3, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 6, size: 'full' }
  ],
  mentoringCoordinator: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 7, size: 'full' }
  ],
  mentor: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'parents-meet-management', enabled: true, order: 6, size: 'full' },
    { id: 'submission-status-report', enabled: true, order: 7, size: 'full' }
  ],
  courseIncharge: [
    { id: 'faculty-profile', enabled: true, order: 1, size: 'full' },
    { id: 'course-marks-entry', enabled: true, order: 2, size: 'full' },
    { id: 'course-attendance-entry', enabled: true, order: 3, size: 'full' }
  ]
};

const roleLabels = {
  hod: 'HOD',
  faculty: 'Faculty',
  administrator: 'Administrator',
  mentoringCoordinator: 'Mentoring Co-ordinator',
  classCoordinator: 'Class Co-ordinator',
  mentor: 'Mentor',
  courseIncharge: 'Course Management',
  student: 'Student'
};

const normalizeRoleName = (role) => {
  if (role === 'mentoring_coordinator') return 'mentoringCoordinator';
  if (role === 'class_coordinator') return 'classCoordinator';
  if (role === 'administrator' || role === 'admin') return 'administrator';
  return role;
};

const assignmentKey = (assignment = {}) => (
  `${assignment.role || ''}:${assignment.division || ''}:${assignment.batch || ''}:${assignment.scope || ''}:${assignment.semester || ''}:${assignment.subjectId || ''}`
);

const normalizeFacultyAssignments = (assignments = [], fallbackUser = {}) => {
  const normalized = Array.isArray(assignments) ? assignments
    .map((assignment) => ({
      role: normalizeRoleName(assignment.role),
      label: assignment.label || roleLabels[normalizeRoleName(assignment.role)] || assignment.role,
      division: assignment.division || '',
      batch: assignment.batch || '',
      scope: assignment.scope || ''
    }))
    .filter((assignment) => ['classCoordinator', 'mentor', 'mentoringCoordinator'].includes(assignment.role)) : [];

  if (normalized.length) return normalized;

  const fallbackRole = normalizeRoleName(fallbackUser.role);
  if (!['classCoordinator', 'mentor', 'mentoringCoordinator'].includes(fallbackRole)) return [];

  return [{
    role: fallbackRole,
    label: roleLabels[fallbackRole],
    division: fallbackUser.assignedDivision || '',
    batch: fallbackUser.assignedBatch || '',
    scope: fallbackRole === 'mentoringCoordinator' ? 'department' : ''
  }];
};

const normalizeIdentity = (value) => String(value || '').trim().toLowerCase();

const getFacultyIdentities = (faculty = {}) => new Set([
  faculty.email,
  faculty.name,
  faculty.id,
  faculty.username,
  faculty.loginId
].map(normalizeIdentity).filter(Boolean));

const facultyMatchesSubject = (subject, identities) => {
  const email = normalizeIdentity(subject.courseInchargeEmail);
  const name = normalizeIdentity(subject.courseInchargeName);
  return Boolean((email && identities.has(email)) || (name && identities.has(name)));
};

const sortDivisionNames = (items) => [...new Set(items.map((item) => String(item || '').trim()).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const loadKnownDivisions = async () => {
  const [studentsSnap, divisionSnap] = await Promise.all([
    getDocs(collection(db, 'students')),
    getDocs(collection(db, 'divisionAssignments'))
  ]);
  const divisions = [];

  studentsSnap.docs.forEach((studentDoc) => {
    const student = studentDoc.data();
    divisions.push(student.division);
  });

  divisionSnap.docs.forEach((divisionDoc) => {
    const division = divisionDoc.data();
    divisions.push(division.division, division.name, divisionDoc.id);
  });

  return sortDivisionNames(divisions);
};

const courseAssignmentFromSubject = ({ subject, semester, faculty, division = '' }) => ({
  role: 'courseIncharge',
  label: 'Course Management',
  semester,
  subjectId: subject.id,
  subjectCode: subject.code || '',
  subjectName: subject.name || '',
  teachingDivision: division || subject.teachingDivision || '',
  division: division || subject.teachingDivision || '',
  scope: 'course',
  courseInchargeEmail: subject.courseInchargeEmail || faculty.email || '',
  courseInchargeName: subject.courseInchargeName || faculty.name || ''
});

const loadCourseAssignmentsForUser = async (faculty = {}) => {
  const identities = getFacultyIdentities(faculty);
  if (!identities.size) return [];

  const [semester1Doc, semester2Doc, knownDivisions] = await Promise.all([
    getDoc(doc(db, 'semesterSubjects', 'semester1')),
    getDoc(doc(db, 'semesterSubjects', 'semester2')),
    loadKnownDivisions()
  ]);

  return [
    { semester: 1, semesterDoc: semester1Doc },
    { semester: 2, semesterDoc: semester2Doc }
  ].flatMap(({ semester, semesterDoc }) => (
    getConfiguredSubjects(semesterDoc.exists() ? semesterDoc.data() : null, semester)
      .filter((subject) => facultyMatchesSubject(subject, identities))
      .flatMap((subject) => {
        const subjectDivision = subject.teachingDivision || '';
        if (subjectDivision) {
          return [courseAssignmentFromSubject({ subject, semester, faculty })];
        }

        if (!knownDivisions.length) {
          return [courseAssignmentFromSubject({ subject, semester, faculty })];
        }

        return knownDivisions.map((division) => courseAssignmentFromSubject({
          subject,
          semester,
          faculty,
          division
        }));
      })
  ));
};

const buildScopedFacultyUser = (baseUser, assignment) => {
  if (!baseUser || !assignment) return baseUser;

  return {
    ...baseUser,
    role: assignment.role,
    assignedDivision: assignment.division || baseUser.assignedDivision || '',
    assignedBatch: assignment.batch || baseUser.assignedBatch || '',
    activeAssignment: assignment,
    activeCourseAssignment: assignment.role === 'courseIncharge' ? assignment : null
  };
};

const formatRoleLabel = (role) => roleLabels[role] || String(role || '').replace(/([A-Z])/g, ' $1').trim();

const formatAssignmentLabel = (assignment = {}) => {
  const base = assignment.label || formatRoleLabel(assignment.role);
  if (assignment.role === 'courseIncharge') {
    const subject = assignment.subjectCode || assignment.subjectName || 'Course';
    const division = assignment.teachingDivision || assignment.division;
    return `${base} - ${subject}${division ? ` Division ${division}` : ''}`;
  }
  if (assignment.role === 'classCoordinator' && assignment.division) return `${base} - Division ${assignment.division}`;
  if (assignment.role === 'mentor' && assignment.batch) return `${base} - Batch ${assignment.batch}`;
  return base;
};

const mergeRequiredWidgets = (role, config) => {
  const requiredWidgets = requiredWidgetsByRole[role] || [];
  if (!requiredWidgets.length || !config?.widgets) return config;

  const existingIds = new Set(config.widgets.map((widget) => widget.id));
  const missingWidgets = requiredWidgets.filter((widget) => !existingIds.has(widget.id));

  if (!missingWidgets.length) return config;

  return {
    ...config,
    widgets: [...config.widgets, ...missingWidgets]
  };
};

const sanitizeDashboardConfig = (role, config) => {
  if (!config?.widgets) return config;
  if (role === 'courseIncharge') return config;

  return {
    ...config,
    widgets: config.widgets.filter((widget) => !['course-marks-entry', 'course-attendance-entry'].includes(widget.id))
  };
};

const UnifiedDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [dashboardData, setDashboardData] = useState({});
  const [facultyAssignments, setFacultyAssignments] = useState([]);
  const [activeFacultyAssignment, setActiveFacultyAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Priority widgets that load immediately (always expanded)
  // Note: stats-overview removed from priority because attendance calculation is slow
  // It will load when user expands it
  const priorityWidgets = [
    'attendance-entry',
    'course-marks-entry',
    'course-attendance-entry',
    'achievement-approvals',
    'leave-approvals'
  ];

  const isPriorityWidget = (widgetId) => priorityWidgets.includes(widgetId);
  const widgetDataLoaded = React.useRef({});

  useEffect(() => {
    loadUserAndConfig();
  }, []);

  const getActiveUser = () => buildScopedFacultyUser(user, activeFacultyAssignment);

  const loadDashboardConfig = async (role) => {
    if (role === 'faculty') {
      return sanitizeDashboardConfig(role, mergeRequiredWidgets(role, defaultDashboardConfigs[role]));
    }

    const configDoc = await getDoc(doc(db, 'dashboardConfigs', role));
    let config;
    
    if (configDoc.exists()) {
      config = configDoc.data();
    } else if (role === 'administrator') {
      const hodConfigDoc = await getDoc(doc(db, 'dashboardConfigs', 'hod'));
      config = hodConfigDoc.exists() ? hodConfigDoc.data() : defaultDashboardConfigs.hod;
    } else {
      config = defaultDashboardConfigs[role];
    }

    return sanitizeDashboardConfig(role, mergeRequiredWidgets(role, config));
  };

  const loadUserAndConfig = async () => {
    try {
      let userData = null;
      let role = null;

      // Check for administrator session (hardcoded admin login)
      const adminSession = sessionStorage.getItem('adminSession');
      if (adminSession) {
        const session = JSON.parse(adminSession);
        userData = session;
        role = 'administrator';
      } else {
        const studentSession = sessionStorage.getItem('studentSession');
        if (studentSession) {
          const session = JSON.parse(studentSession);
          const studentDoc = await getDoc(doc(db, 'students', session.rollNumber));
          if (studentDoc.exists()) {
            userData = studentDoc.data();
            role = 'student';
          }
        } else {
          // Check for staff/user session (from username/password login)
          const userSession = sessionStorage.getItem('userSession');
          if (userSession) {
            const session = JSON.parse(userSession);
            userData = session;
            const dutyAssignments = session.userType === 'faculty'
              ? normalizeFacultyAssignments(session.assignments, session)
              : [];
            const courseAssignments = session.userType === 'faculty'
              ? await loadCourseAssignmentsForUser(session)
              : [];
            const assignments = [...dutyAssignments, ...courseAssignments];

            if (assignments.length) {
              setFacultyAssignments(assignments);
              setActiveFacultyAssignment(assignments[0]);
              role = assignments[0].role;
              userData = buildScopedFacultyUser(session, assignments[0]);
            } else {
              setFacultyAssignments([]);
              setActiveFacultyAssignment(null);
              role = normalizeRoleName(session.role);
            }
          } else {
            // Fallback to email-based authentication (old method)
            const currentUser = auth.currentUser;
            if (!currentUser) {
              navigate('/');
              return;
            }
            
            // Only try to use email if it exists (not null for anonymous users)
            if (currentUser.email) {
              const staffDoc = await getDoc(doc(db, 'users', currentUser.email));
              if (staffDoc.exists()) {
                userData = staffDoc.data();
                const dutyAssignments = normalizeFacultyAssignments(userData.assignments, userData);
                const courseAssignments = await loadCourseAssignmentsForUser({
                  ...userData,
                  id: staffDoc.id,
                  email: userData.email || currentUser.email
                });
                const assignments = [...dutyAssignments, ...courseAssignments];

                if (assignments.length) {
                  const baseUser = {
                    ...userData,
                    id: staffDoc.id,
                    email: userData.email || currentUser.email,
                    userType: 'faculty'
                  };
                  setFacultyAssignments(assignments);
                  setActiveFacultyAssignment(assignments[0]);
                  role = assignments[0].role;
                  userData = buildScopedFacultyUser(baseUser, assignments[0]);
                } else {
                  setFacultyAssignments([]);
                  setActiveFacultyAssignment(null);
                  role = normalizeRoleName(userData.role);
                }
              }
            }
          }
        }
      }

      if (!userData) {
        navigate('/');
        return;
      }

      setUser(userData);
      setUserRole(role);

      const config = await loadDashboardConfig(role);

      // Safety check - ensure config exists
      if (!config || !config.widgets) {
        console.error('Dashboard config not found for role:', role);
        setError('Dashboard configuration error. Please contact administrator.');
        setLoading(false);
        return;
      }

      setDashboardConfig(config);
      await loadPriorityData(role, userData); // Use fast priority loading!
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  const switchFacultyAssignment = async (assignment) => {
    if (!assignment || assignmentKey(activeFacultyAssignment) === assignmentKey(assignment)) return;

    setLoading(true);
    setError('');
    widgetDataLoaded.current = {};

    try {
      const scopedUser = buildScopedFacultyUser(user, assignment);
      const config = await loadDashboardConfig(assignment.role);

      if (!config || !config.widgets) {
        setError('Dashboard configuration error. Please contact administrator.');
        return;
      }

      setActiveFacultyAssignment(assignment);
      setUser(scopedUser);
      setUserRole(assignment.role);
      setDashboardConfig(config);
      await loadPriorityData(assignment.role, scopedUser);
    } catch (error) {
      console.error('Error switching dashboard role:', error);
      setError('Could not switch dashboard role.');
    } finally {
      setLoading(false);
    }
  };

  // ULTRA-FAST priority data loading - skips heavy calculations
  const loadPriorityData = async (role, userData) => {
    const data = {};

    try {
      switch (role) {
        case 'hod':
        case 'mentoringCoordinator':
          // ONLY load counts - no detailed data or loops!
          const studentsSnap = await getDocs(collection(db, 'students'));
          data.studentCount = studentsSnap.size;
          
          // Load pending achievement COUNT only
          const achievementsSnap = await getDocs(query(
            collection(db, 'achievements'),
            where('status', '==', 'pending')
          ));
          data.pendingAchievementsCount = achievementsSnap.size;
          data.pendingAchievements = achievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // Basic stats without attendance loop (calculate later if needed)
          data.stats = {
            totalStudents: data.studentCount,
            avgAttendance: 0, // Will be calculated when stats widget expands
            atRisk: 0, // Will be calculated when needed
            totalAchievements: data.pendingAchievementsCount
          };
          break;

        case 'classCoordinator':
          // Load only division student count
          const divStudentsSnap = await getDocs(query(
            collection(db, 'students'),
            where('division', '==', userData.assignedDivision)
          ));
          data.divisionStudentCount = divStudentsSnap.size;

          // Load pending leaves COUNT only
          const leavesSnap = await getDocs(query(
            collection(db, 'leaves'),
            where('status', '==', 'pending')
          ));
          data.pendingLeavesCount = leavesSnap.size;
          data.pendingLeaves = leavesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(leave => divStudentsSnap.docs.some(s => s.data().rollNumber === leave.rollNumber));

          data.stats = {
            totalStudents: data.divisionStudentCount,
            avgAttendance: 0,
            atRisk: 0
          };
          break;

        case 'mentor':
          // Load only mentee count
          const menteesSnap = await getDocs(query(
            collection(db, 'students'),
            where('batch', '==', userData.assignedBatch)
          ));
          data.menteeCount = menteesSnap.size;

          data.stats = {
            totalMentees: data.menteeCount
          };
          break;

        case 'student':
          // Minimal student data
          const attendanceDoc = await getDoc(doc(db, 'attendance', userData.rollNumber));
          if (attendanceDoc.exists()) {
            data.attendance = attendanceDoc.data();
          }

          const iaTest1Doc = await getDoc(doc(db, 'iaTest1', userData.rollNumber));
          if (iaTest1Doc.exists()) {
            data.iaTest1 = iaTest1Doc.data();
          }

          const iaTest2Doc = await getDoc(doc(db, 'iaTest2', userData.rollNumber));
          if (iaTest2Doc.exists()) {
            data.iaTest2 = iaTest2Doc.data();
          }
          break;

        case 'administrator':
          // Administrator: Load minimal data
          const adminStudentsSnap = await getDocs(collection(db, 'students'));
          data.studentCount = adminStudentsSnap.size;
          
          const adminStaffSnap = await getDocs(collection(db, 'users'));
          data.staffCount = adminStaffSnap.size;

          const adminAchievementsSnap = await getDocs(query(
            collection(db, 'achievements'),
            where('status', '==', 'pending')
          ));
          data.pendingAchievementsCount = adminAchievementsSnap.size;
          data.pendingAchievements = adminAchievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          data.stats = {
            totalStudents: data.studentCount,
            totalStaff: data.staffCount,
            avgAttendance: 0,
            atRisk: 0,
            totalAchievements: data.pendingAchievementsCount
          };
          break;
      }

      setDashboardData(data);
    } catch (error) {
      console.error('Error loading priority data:', error);
    }
  };

  // Original function kept for reference but not used
  const loadDashboardData = async (role, userData) => {
    const data = {};

    try {
      switch (role) {
        case 'hod':
        case 'mentoringCoordinator':
          const allStudentsSnap = await getDocs(collection(db, 'students'));
          data.allStudents = allStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          let totalAttendance = 0;
          let atRiskCount = 0;
          for (const student of data.allStudents) {
            const attendanceDoc = await getDoc(doc(db, 'attendance', student.rollNumber));
            if (attendanceDoc.exists()) {
              const att = attendanceDoc.data();
              totalAttendance += att.overallPercentage || 0;
              if (att.overallPercentage < 75) atRiskCount++;
            }
          }

          data.stats = {
            totalStudents: data.allStudents.length,
            avgAttendance: data.allStudents.length > 0 ? Math.round(totalAttendance / data.allStudents.length) : 0,
            atRisk: atRiskCount,
            totalAchievements: 0
          };

          // HOD staff loading removed - moved to StaffManagementWidget for lazy loading
          // This improves dashboard load time significantly

          const achievementsSnap = await getDocs(query(
            collection(db, 'achievements'),
            where('status', '==', 'pending')
          ));
          data.pendingAchievements = achievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          data.stats.totalAchievements = achievementsSnap.size;
          break;

        case 'classCoordinator':
          const divStudentsSnap = await getDocs(query(
            collection(db, 'students'),
            where('division', '==', userData.assignedDivision)
          ));
          data.divisionStudents = divStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          let divTotalAtt = 0;
          let divAtRisk = 0;
          for (const student of data.divisionStudents) {
            const attendanceDoc = await getDoc(doc(db, 'attendance', student.rollNumber));
            if (attendanceDoc.exists()) {
              const att = attendanceDoc.data();
              divTotalAtt += att.overallPercentage || 0;
              if (att.overallPercentage < 75) divAtRisk++;
            }
          }

          data.stats = {
            totalStudents: data.divisionStudents.length,
            avgAttendance: data.divisionStudents.length > 0 ? Math.round(divTotalAtt / data.divisionStudents.length) : 0,
            atRisk: divAtRisk
          };

          const leavesSnap = await getDocs(query(
            collection(db, 'leaves'),
            where('status', '==', 'pending')
          ));
          data.pendingLeaves = leavesSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(leave => data.divisionStudents.some(s => s.rollNumber === leave.rollNumber));
          break;

        case 'mentor':
          const menteesSnap = await getDocs(query(
            collection(db, 'students'),
            where('batch', '==', userData.assignedBatch)
          ));
          const menteesData = [];
          for (const studentDoc of menteesSnap.docs) {
            const studentData = studentDoc.data();
            const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
            menteesData.push({
              ...studentData,
              attendance: attendanceDoc.exists() ? attendanceDoc.data() : null
            });
          }
          data.mentees = menteesData;

          const menteeAchievementsSnap = await getDocs(query(
            collection(db, 'achievements'),
            where('status', '==', 'pending'),
            where('batch', '==', userData.assignedBatch)
          ));
          data.pendingAchievements = menteeAchievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          break;

        case 'student':
          const attendanceDoc = await getDoc(doc(db, 'attendance', userData.rollNumber));
          data.attendance = attendanceDoc.exists() ? attendanceDoc.data() : null;

          const iaTest1Doc = await getDoc(doc(db, 'iaTest1', userData.rollNumber));
          data.iaTest1 = iaTest1Doc.exists() ? iaTest1Doc.data() : null;

          const iaTest2Doc = await getDoc(doc(db, 'iaTest2', userData.rollNumber));
          data.iaTest2 = iaTest2Doc.exists() ? iaTest2Doc.data() : null;

          const achievementsSnap2 = await getDocs(query(
            collection(db, 'achievements'),
            where('rollNumber', '==', userData.rollNumber)
          ));
          data.achievements = achievementsSnap2.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          const mentorQuery = await getDocs(query(
            collection(db, 'users'),
            where('role', '==', 'mentor'),
            where('assignedBatch', '==', userData.batch)
          ));
          data.mentorName = mentorQuery.empty ? 'Not Assigned' : mentorQuery.docs[0].data().name;

          const coordinatorQuery = await getDocs(query(
            collection(db, 'users'),
            where('role', '==', 'class_coordinator'),
            where('assignedDivision', '==', userData.division)
          ));
          data.coordinatorName = coordinatorQuery.empty ? 'Not Assigned' : coordinatorQuery.docs[0].data().name;
          break;

        case 'administrator':
          // Administrator has access to all system data
          // Load all students and all staff for admin dashboard
          const adminStudentsSnap = await getDocs(collection(db, 'students'));
          data.allStudents = adminStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const adminStaffSnap = await getDocs(collection(db, 'users'));
          data.allStaff = adminStaffSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // Calculate stats for administrator
          let adminTotalAtt = 0;
          let adminAtRisk = 0;
          for (const student of data.allStudents) {
            const attendanceDoc = await getDoc(doc(db, 'attendance', student.rollNumber));
            if (attendanceDoc.exists()) {
              const att = attendanceDoc.data();
              adminTotalAtt += att.overallPercentage || 0;
              if (att.overallPercentage < 75) adminAtRisk++;
            }
          }

          data.stats = {
            totalStudents: data.allStudents.length,
            totalStaff: data.allStaff.length,
            avgAttendance: data.allStudents.length > 0 ? Math.round(adminTotalAtt / data.allStudents.length) : 0,
            atRisk: adminAtRisk,
            totalAchievements: 0
          };

          const adminAchievementsSnap = await getDocs(query(
            collection(db, 'achievements'),
            where('status', '==', 'pending')
          ));
          data.pendingAchievements = adminAchievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          data.stats.totalAchievements = adminAchievementsSnap.size;
          break;
      }

      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  // Lazy load specific widget data on demand
  const loadWidgetData = async (widgetId) => {
    // Check if already loaded
    if (widgetDataLoaded.current[widgetId]) {
      return;
    }

    try {
      const activeUser = getActiveUser();

      switch (widgetId) {
        case 'stats-overview':
          // Calculate attendance stats only when stats widget is expanded
          if (userRole === 'hod' || userRole === 'mentoringCoordinator' || userRole === 'administrator') {
            if (!dashboardData.allStudents) {
              const studentsSnap = await getDocs(collection(db, 'students'));
              const students = studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
              
              // Calculate attendance - but use Promise.all for parallel loading!
              const attendancePromises = students.map(student => 
                getDoc(doc(db, 'attendance', student.rollNumber))
              );
              
              const attendanceDocs = await Promise.all(attendancePromises);
              
              let totalAttendance = 0;
              let atRiskCount = 0;
              let studentsWithAttendance = 0;
              
              attendanceDocs.forEach(attendanceDoc => {
                if (attendanceDoc.exists()) {
                  const att = attendanceDoc.data();
                  totalAttendance += att.overallPercentage || 0;
                  if (att.overallPercentage < 75) atRiskCount++;
                  studentsWithAttendance++;
                }
              });
              
              setDashboardData(prev => ({
                ...prev,
                allStudents: students,
                stats: {
                  ...prev.stats,
                  avgAttendance: studentsWithAttendance > 0 ? Math.round(totalAttendance / studentsWithAttendance) : 0,
                  atRisk: atRiskCount
                }
              }));
            }
          }
          break;

        case 'student-search':
          if (!dashboardData.allStudents) {
            const studentsSnap = await getDocs(collection(db, 'students'));
            setDashboardData(prev => ({
              ...prev,
              allStudents: studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            }));
          }
          break;

        case 'staff-management':
          if (!dashboardData.allStaff) {
            const staffSnap = await getDocs(collection(db, 'users'));
            setDashboardData(prev => ({
              ...prev,
              allStaff: staffSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            }));
          }
          break;

        case 'mentee-list':
          if (!dashboardData.mentees && activeUser?.assignedBatch) {
            const menteesSnap = await getDocs(query(
              collection(db, 'students'),
              where('batch', '==', activeUser.assignedBatch)
            ));
            setDashboardData(prev => ({
              ...prev,
              mentees: menteesSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            }));
          }
          break;

        case 'division-analytics':
          if (!dashboardData.divisionStudents && activeUser?.assignedDivision) {
            const divStudentsSnap = await getDocs(query(
              collection(db, 'students'),
              where('division', '==', activeUser.assignedDivision)
            ));
            setDashboardData(prev => ({
              ...prev,
              divisionStudents: divStudentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            }));
          }
          break;
          
       
        // Add more widget cases as needed
        default:
          console.log(`No lazy loading defined for widget: ${widgetId}`);
      }

      widgetDataLoaded.current[widgetId] = true;
    } catch (error) {
      console.error(`Error loading widget ${widgetId}:`, error);
    }
  };

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('studentSession');
      sessionStorage.removeItem('adminSession');
      sessionStorage.removeItem('userSession');
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderWidget = (widgetConfig) => {
    if (!widgetConfig.enabled) return null;

    const { id, size } = widgetConfig;
    const activeUser = getActiveUser();
    
    // CRITICAL: Widget Maker is EXCLUSIVELY for Administrator only
    if (id === 'widget-maker' && userRole !== 'administrator') {
      return null; // Don't render at all for non-administrators
    }

    if (['course-marks-entry', 'course-attendance-entry'].includes(id) && userRole !== 'courseIncharge') {
      return null;
    }

    let widgetTitle = '';
    let widgetContent = null;
    const widgetProps = {
      canViewDashboard: dashboardConfig?.permissions?.canViewAllStudents,
      userRole,
      assignedDivision: activeUser?.assignedDivision,
      assignedBatch: activeUser?.assignedBatch
    };

    switch (id) {
      case 'stats-overview':
        widgetTitle = 'Overview Statistics';
        widgetContent = <StatsOverviewWidget stats={dashboardData.stats || {}} />;
        break;

      case 'student-search':
        widgetTitle = 'Student Search';
        widgetContent = <StudentSearchWidget 
          onSearchStudent={async (query, mode) => {
            // Search logic here
            return null;
          }}
          {...widgetProps}
        />;
        break;

      case 'mentee-list':
        widgetTitle = 'My Mentees';
        widgetContent = <MenteeListWidget mentees={dashboardData.mentees || []} />;
        break;

      case 'attendance-card':
        widgetTitle = 'My Attendance';
        widgetContent = <AttendanceCardWidget attendance={dashboardData.attendance} />;
        break;

      case 'achievement-approvals':
        widgetTitle = 'Pending Achievements';
        widgetContent = <AchievementApprovalsWidget achievements={dashboardData.pendingAchievements || []} userEmail={activeUser?.email} {...widgetProps} />;
        break;

      case 'leave-approvals':
        widgetTitle = 'Pending Leave Requests';
        widgetContent = <LeaveApprovalsWidget leaves={dashboardData.pendingLeaves || []} {...widgetProps} />;
        break;

      case 'messaging':
        widgetTitle = 'Messaging';
        widgetContent = <MessagingWidget userRole={userRole} />;
        break;

      case 'staff-management':
        widgetTitle = 'Staff Management';
        widgetContent = <StaffManagementWidget allStaff={dashboardData.allStaff || []} {...widgetProps} />;
        break;

      case 'faculty-roles':
        widgetTitle = 'Faculty Management';
        widgetContent = <FacultyRolesWidget />;
        break;

      case 'faculty-profile':
        widgetTitle = 'My Faculty Profile';
        widgetContent = <FacultyProfileWidget userData={activeUser} />;
        break;

      case 'system-archive':
        widgetTitle = 'Backup, Reset & Annual Analysis';
        widgetContent = <SystemArchiveWidget />;
        break;

      case 'student-management':
        widgetTitle = 'Student Management';
        widgetContent = <StudentManagementWidget />;
        break;

      case 'semester-subjects':
        widgetTitle = 'Semester Subject Management';
        widgetContent = <SemesterSubjectsWidget />;
        break;

      case 'analytics-charts':
        widgetTitle = 'Analytics';
        widgetContent = <AnalyticsChartsWidget stats={dashboardData.stats || {}} />;
        break;

      case 'sheets-sync':
        widgetTitle = 'Google Sheets Sync';
        widgetContent = <SheetsSyncWidget />;
        break;

      case 'attendance-entry':
        widgetTitle = 'Attendance Entry';
        widgetContent = (
          <AttendanceEntryWidget
            assignedDivision={activeUser?.assignedDivision}
            userName={activeUser?.name}
          />
        );
        break;

      case 'course-marks-entry':
        widgetTitle = 'Course Marks Entry';
        widgetContent = (
          <CourseMarksEntryWidget
            userEmail={activeUser?.email}
            userName={activeUser?.name}
            userId={activeUser?.id}
            username={activeUser?.username || activeUser?.loginId}
            assignedDivision={activeUser?.assignedDivision}
            courseAssignment={activeUser?.activeCourseAssignment}
          />
        );
        break;

      case 'course-attendance-entry':
        widgetTitle = 'Course Attendance Entry';
        widgetContent = (
          <CourseAttendanceEntryWidget
            userEmail={activeUser?.email}
            userName={activeUser?.name}
            userId={activeUser?.id}
            username={activeUser?.username || activeUser?.loginId}
            assignedDivision={activeUser?.assignedDivision}
            courseAssignment={activeUser?.activeCourseAssignment}
          />
        );
        break;

      case 'submission-status-report':
        widgetTitle = 'Submission Status Reports';
        widgetContent = (
          <SubmissionStatusWidget
            userRole={userRole}
            assignedDivision={activeUser?.assignedDivision}
            assignedBatch={activeUser?.assignedBatch}
            userName={activeUser?.name}
          />
        );
        break;

      case 'batch-assignments':
        widgetTitle = 'Batch Management';
        widgetContent = <BatchAssignmentsWidget />;
        break;

      case 'mentor-overview':
        widgetTitle = 'Mentor Overview';
        widgetContent = <MentorOverviewWidget allStaff={dashboardData.allStaff || []} />;
        break;

      case 'unassigned-students':
        widgetTitle = 'Unassigned Students';
        widgetContent = <UnassignedStudentsWidget />;
        break;

      case 'division-analytics':
        widgetTitle = 'Division Analytics';
        widgetContent = <DivisionAnalyticsWidget stats={dashboardData.stats || {}} />;
        break;

      case 'mentee-details':
        widgetTitle = 'Mentee Details';
        widgetContent = <MenteeDetailsWidget />;
        break;

      case 'attendance-alerts':
        widgetTitle = 'Attendance Alerts';
        widgetContent = <AttendanceAlertsWidget mentees={dashboardData.mentees || []} />;
        break;

      case 'parents-meet-management':
        widgetTitle = 'Parents Meet Management';
        widgetContent = (
          <ParentsMeetManagementWidget
            assignedBatch={activeUser?.assignedBatch}
            userEmail={activeUser?.email}
            userName={activeUser?.name}
          />
        );
        break;

      case 'student-profile':
        widgetTitle = 'Profile';
        widgetContent = <StudentProfileWidget userData={activeUser} />;
        break;

      case 'ia-test1-card':
        widgetTitle = 'IA Test 1';
        widgetContent = <IaTest1CardWidget iaTest1={dashboardData.iaTest1} />;
        break;

      case 'ia-test2-card':
        widgetTitle = 'IA Test 2';
        widgetContent = <IaTest2CardWidget iaTest2={dashboardData.iaTest2} />;
        break;

      case 'quick-actions':
        widgetTitle = 'Quick Actions';
        widgetContent = <QuickActionsWidget rollNumber={activeUser?.rollNumber} />;
        break;

      case 'achievements-list':
        widgetTitle = 'My Achievements';
        widgetContent = <AchievementsListWidget achievements={dashboardData.achievements || []} />;
        break;

      case 'leaderboard':
        widgetTitle = 'Leaderboard';
        widgetContent = <LeaderboardWidget />;
        break;

      case 'notifications':
        widgetTitle = 'Notifications';
        widgetContent = <NotificationsWidget />;
        break;

      case 'student-id-card':
        widgetTitle = 'Student ID';
        widgetContent = <StudentIdCardWidget userData={activeUser} mentorName={dashboardData.mentorName} coordinatorName={dashboardData.coordinatorName} />;
        break;

      case 'widget-maker':
        widgetTitle = 'Widget Maker';
        // CRITICAL: Widget Maker is ONLY available to Administrator
        if (userRole === 'administrator') {
          widgetContent = <WidgetMaker />;
        } else {
          widgetContent = (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg font-semibold mb-2">🔒 Administrator Access Required</p>
              <p className="text-sm">Widget Maker is only available to system administrators.</p>
            </div>
          );
        }
        break;

      // CUSTOM WIDGETS CASES    case 'attendancereport':

case 'studentlist':
  widgetTitle = 'Student List';
  widgetContent = <StudentListWidget />;
  break;

    case 'noticesandannouncements':
      widgetTitle = 'NoticesAndAnnouncements';
      widgetContent = <NoticesAndAnnouncementsWidget
    userRole={activeUser.role}
    userEmail={activeUser.email}
    userName={activeUser.name}
   />;
      break;

    case 'warningletters':
      widgetTitle = 'WarningLetters';
      widgetContent = <WarningLettersWidget 
  userRole={activeUser?.role}
  userDivision={activeUser?.assignedDivision}
  userName={activeUser?.name}
/>;
      break;

    case 'forminfo':
      widgetTitle = 'Forminfo';
      widgetContent = <ForminfoWidget />;
      break;

    case 'divisionassignments':
      widgetTitle = 'Division Management';
      widgetContent = <DivisionAssignmentsWidget />;
      break;

    case 'pendingachievements':
      widgetTitle = 'PendingAchievements';
      widgetContent = <PendingAchievementsWidget />;
      break;
      widgetTitle = 'AttendanceReport';
      widgetContent = <AttendanceReportWidget />;
      break;

    case 'topperformers':
      widgetTitle = 'Topperformers';
      widgetContent = <TopperformersWidget />;
      break;

    case 'nanomessaging':
      widgetTitle = 'NanoMessaging';
      widgetContent = <NanoMessagingWidget />;
      break;
    case 'slowaveragefastlearners':
      widgetTitle = 'SlowAverageFastLearners';
      widgetContent = <SlowAverageFastLearnersWidget />;
      break;
case 'mentee-yaadi':
  widgetTitle = 'My Mentees';
  widgetContent = <MenteeYaadiWidget 
    userEmail={activeUser?.email}
    userName={activeUser?.name}
  />;
  break;
      // Auto-generated custom widget cases will be added here by the backend

      default:
        // Handle custom widgets
        const customWidget = widgetConfig.customConfig;
        if (customWidget) {
          widgetTitle = customWidget.name || customWidget.title || 'Custom Widget';
          // Try to dynamically import the widget
          try {
            // The widget component will be loaded by the import at the top
            // For now, show a placeholder
            widgetContent = (
              <div className="text-center py-8 text-gray-400">
                <p>Custom widget "{customWidget.name}" is loading...</p>
                <p className="text-sm mt-2">Refresh the page to see the widget.</p>
              </div>
            );
          } catch (error) {
            widgetContent = (
              <div className="text-center py-8 text-red-400">
                <p>Error loading custom widget</p>
              </div>
            );
          }
        } else {
          widgetContent = (
            <div className="text-center py-8 text-gray-400">
              Widget "{id}" not yet implemented
            </div>
          );
        }
    }

    const isPriority = isPriorityWidget(id);

    return (
      <WidgetWrapper 
        key={id} 
        id={id} 
        title={widgetTitle} 
        size={size}
        priority={isPriority}
        defaultCollapsed={!isPriority}
        onExpand={() => loadWidgetData(id)}
      >
        {widgetContent}
      </WidgetWrapper>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user || !dashboardConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <p className="text-xl text-gray-900 font-semibold mb-2">Unable to load dashboard</p>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          <p className="text-sm text-gray-600 mb-4">
            Please check the browser console (F12) for detailed error messages.
          </p>
          <div className="space-y-2">
            <button 
              onClick={() => window.location.reload()} 
              className="block w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
            <button 
              onClick={() => navigate('/')} 
              className="block w-full px-4 py-2 text-blue-600 hover:underline"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sortedWidgets = [...dashboardConfig.widgets].sort((a, b) => a.order - b.order);
  const activeUser = getActiveUser();
  const showFacultyTabs = activeUser?.userType === 'faculty' && facultyAssignments.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {userRole === 'student' 
                  ? `${activeUser.firstName || ''} ${activeUser.surname || ''}`.trim() || activeUser.name
                  : activeUser.name || 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-600 capitalize">
                {formatRoleLabel(userRole)} Dashboard
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {userRole === 'administrator' && (
                <button
                  onClick={() => navigate('/admin-dashboard')}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  title="Admin Dashboard"
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Admin Dashboard</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {showFacultyTabs && (
          <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 pb-3">
            {facultyAssignments.map((assignment) => {
              const active = assignmentKey(assignment) === assignmentKey(activeFacultyAssignment);

              return (
                <button
                  key={assignmentKey(assignment)}
                  type="button"
                  onClick={() => switchFacultyAssignment(assignment)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {formatAssignmentLabel(assignment)}
                </button>
              );
            })}
          </div>
        )}

        {sortedWidgets.length === 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-6 py-8 text-center text-amber-800">
            No faculty duty is assigned yet.
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {sortedWidgets.map(widgetConfig => renderWidget(widgetConfig))}
          </div>
        )}
      </main>
    </div>
  );
};

export default UnifiedDashboard;
